import React, { useState, useRef, useEffect } from "react";
import { wsRequest } from "./wsClient.js";
import Footer from "./Footer.jsx";
import { useNavigate } from "./Navigation.jsx";

// Reached via the link in the "Password Reset" email content box
// (see requestPasswordReset on the backend) — /reset-password?token=...
// Not part of AuthForm.jsx's own login/signup modes, since this is
// opened from an email link, not from within the app's own
// navigation.
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // See InstallPage.jsx's identical confirmPasswordRef for the full
  // reasoning — feeds the browser's own native validity state so this
  // integrates with theme.css's red/green border styling the same way
  // a plain required/minLength failure would.
  const confirmPasswordRef = useRef(null);
  useEffect(() => {
    if (confirmPasswordRef.current) {
      confirmPasswordRef.current.setCustomValidity(confirmPassword && confirmPassword !== password ? "Passwords don't match." : "");
    }
  }, [password, confirmPassword]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await wsRequest("auth.resetPassword", { token, newPassword: password });
      setDone(true);
    } catch (err) {
      setError(err.message || "Could not reach backend.");
    }
    setBusy(false);
  };

  return (
    <div style={{ color: "var(--text-primary)", minHeight: "100vh" }}>
      <div style={{ maxWidth: 420, margin: "0 auto", padding: "40px 20px" }}>
        <h2>Reset your password</h2>

        {!token ? (
          <p style={{ opacity: 0.85, lineHeight: 1.6 }}>
            This link is missing its reset token — please use the link from the password reset email directly,
            rather than navigating here on its own.
          </p>
        ) : done ? (
          <>
            <p style={{ opacity: 0.85, lineHeight: 1.6 }}>Your password has been reset.</p>
            <button onClick={() => navigate("/")}>Go to login</button>
          </>
        ) : (
          <form onSubmit={submit}>
            <p style={{ opacity: 0.85, lineHeight: 1.6, marginTop: 0 }}>Enter a new password below.</p>
            <input
              type="password"
              placeholder="New password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", fontSize: 14, marginBottom: 10, boxSizing: "border-box" }}
            />
            <input
              ref={confirmPasswordRef}
              type="password"
              placeholder="Confirm new password"
              required
              minLength={8}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", fontSize: 14, marginBottom: 10, boxSizing: "border-box" }}
            />
            <button type="submit" disabled={busy}>
              {busy ? "…" : "Reset password"}
            </button>
            {error && <div style={{ color: "var(--accent-red)", fontSize: 13, marginTop: 8 }}>{error}</div>}
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}
